import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Award, Loader2, CheckCircle2, ExternalLink, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", recognition_title: "", category: "", description: "",
  awarded_date: "", country: "", city: "", website: "", social: "", lang: "sk",
};

export const AdminRecognitions = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [form, setForm] = useState(EMPTY);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [validated, setValidated] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ping, setPing] = useState(null);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setValidated(false); setMsg(null); };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/hof/recognitions`, auth);
      setList(r.data.recognitions || []);
    } catch (e) { /* noop */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const checkPing = async () => {
    setPing("loading");
    try {
      const r = await axios.get(`${API}/admin/hof/ping`, auth);
      setPing(r.data.ok ? `OK (${r.data.body?.env || "?"})` : `Chyba: ${r.data.detail || r.data.status}`);
    } catch (e) { setPing("Chyba spojenia"); }
  };

  const errText = (e) => e?.response?.data?.detail || "Nepodarilo sa odoslať. Skúste znova.";

  const dryRun = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/hof/recognition?dry_run=true`, form, auth);
      if (r.data.dry_run) { setValidated(true); setMsg({ type: "ok", text: `Validácia OK (${r.data.env || "?"}) — pripravené na publikovanie.` }); }
    } catch (e) { setValidated(false); setMsg({ type: "err", text: errText(e) }); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!validated) return;
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/hof/recognition`, form, auth);
      setMsg({ type: "ok", text: "Uznanie publikované v Tigik Sieni slávy.", url: r.data.public_url });
      setForm(EMPTY); setValidated(false);
      load();
    } catch (e) { setMsg({ type: "err", text: errText(e) }); }
    finally { setBusy(false); }
  };

  const canValidate = form.name.trim().length >= 2 && form.recognition_title.trim().length >= 2;

  return (
    <div className="space-y-8" data-testid="admin-recognitions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><Award size={22} className="text-[#D4AF37]" /> Uznania — Sieň slávy</h2>
          <p className="text-white/40 text-sm mt-1">Publikujte uznanie (napr. „Zvárač roka 2026") priamo do Tigik Siene slávy. Každé odoslanie = nové uznanie.</p>
        </div>
        <button onClick={checkPing} data-testid="recognition-ping-btn"
          className="inline-flex items-center gap-2 border border-white/15 text-white/70 px-3 py-2 text-xs hover:border-[#D4AF37]/50 hover:text-white transition-colors">
          <ShieldCheck size={14} /> Test spojenia {ping && ping !== "loading" ? `· ${ping}` : ""}{ping === "loading" ? <Loader2 size={13} className="animate-spin" /> : ""}
        </button>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Meno / firma *" testid="rec-name" value={form.name} onChange={set("name")} placeholder="Ján Novák" />
          <Field label="Názov uznania *" testid="rec-title" value={form.recognition_title} onChange={set("recognition_title")} placeholder="Zvárač roka 2026" />
          <Field label="Kategória" testid="rec-category" value={form.category} onChange={set("category")} placeholder="Osobnosť roka" />
          <Field label="Dátum udelenia" testid="rec-date" type="date" value={form.awarded_date} onChange={set("awarded_date")} />
          <Field label="Krajina" testid="rec-country" value={form.country} onChange={set("country")} placeholder="SK" />
          <Field label="Mesto" testid="rec-city" value={form.city} onChange={set("city")} placeholder="Bratislava" />
          <Field label="Web" testid="rec-website" value={form.website} onChange={set("website")} placeholder="https://…" />
          <Field label="Sociálna sieť" testid="rec-social" value={form.social} onChange={set("social")} placeholder="LinkedIn / IG" />
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1.5">Popis (prečo bolo uznanie udelené)</label>
          <textarea data-testid="rec-description" value={form.description} onChange={set("description")} rows={3} maxLength={500}
            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none resize-none"
            placeholder="Za výnimočný prínos v obore." />
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1.5">Jazyk</label>
          <select data-testid="rec-lang" value={form.lang} onChange={set("lang")}
            className="bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none">
            {["sk", "en", "cs", "de", "pl"].map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>

        {msg && (
          <div data-testid="recognition-msg" className={`flex items-center gap-2 text-sm px-3 py-2 border ${msg.type === "ok" ? "border-[#D4AF37]/40 text-[#F2E3C6] bg-[#D4AF37]/5" : "border-red-500/40 text-red-300 bg-red-500/5"}`}>
            {msg.type === "ok" ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
            <span>{msg.text}</span>
            {msg.url && <a href={msg.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline ml-1">zobraziť <ExternalLink size={12} /></a>}
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-1">
          <button onClick={dryRun} disabled={!canValidate || busy} data-testid="recognition-validate-btn"
            className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {busy && !validated ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Overiť (dry-run)
          </button>
          <button onClick={publish} disabled={!validated || busy} data-testid="recognition-publish-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2 text-sm hover:bg-[#e5c14f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {busy && validated ? <Loader2 size={15} className="animate-spin" /> : <Award size={15} />} Publikuj v Tigik HoF
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-display text-lg mb-3">Publikované uznania {list.length > 0 ? `(${list.length})` : ""}</h3>
        {list.length === 0 ? (
          <p className="text-white/35 text-sm py-6">Zatiaľ ste nepublikovali žiadne uznanie.</p>
        ) : (
          <div className="space-y-2" data-testid="recognition-list">
            {list.map((r) => (
              <div key={r.id} className="border border-white/10 bg-[#0a0a0a] px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{r.recognition_title} <span className="text-white/40 font-normal">— {r.name}</span></p>
                  <p className="text-white/35 text-xs mt-0.5">{r.category ? `${r.category} · ` : ""}{(r.awarded_date || "").slice(0, 10)} · {r.env || "?"}</p>
                </div>
                {r.public_url && (
                  <a href={r.public_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#D4AF37] text-xs hover:underline shrink-0">
                    zobraziť <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, testid, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label className="block text-xs text-white/50 mb-1.5">{label}</label>
    <input type={type} data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
  </div>
);

export default AdminRecognitions;
